import React, {
	useEffect,
	useCallback,
	useRef,
	forwardRef,
	type AbstractComponent,
} from 'react'
import { styled } from 'styled-components'
import {
	useSelectedAudioOutput,
	useMissionPauseForRef,
} from '../../sound/hooks'
import { type MediaRef, VOLUMES } from '../../utility'
import { useVolumeOf } from '../../context'
import { useIsSilent } from '../../context/provider'

const StyledVideo = styled.video`
	height: 100%;
`
type VideoProps = {
	url: string,
	loop?: boolean,
	onEnded?: () => mixed,
	onLoadedData?: () => mixed,
	className?: string,
	autoPlay?: boolean,
	muted?: boolean,
	onPlay?: () => void,
	onPause?: () => void,
}

/**
 * A component that will render a video connected to the mission.
 * It will use the video volume, the selected output, and pause when the mission
 * is paused. It will also not render sound if the mission player should be silent
 * (useIsSilent())
 * @param {VideoProps} props a url, plus other props that a video html element can take
 * @param {Ref<any>|null} ref a ref that can be defined by the calling component. If no ref is
 * provided, a ref will be created.
 * @returns {React$Node} a rendered video tag
 */
export default (forwardRef(function Video(
	{ url, muted, onPlay, ...extraProps }: VideoProps,
	ref:
		| { current: null | HTMLVideoElement }
		| ((null | HTMLVideoElement) => mixed)
): React$Node {
	const newRef: { current: HTMLVideoElement | null } = useRef(null)
	const videoRef = ref || newRef
	const isSilent = useIsSilent()
	useMissionPauseForRef(((videoRef: any): MediaRef), {
		autoPlay: extraProps.autoPlay,
	})
	useSelectedAudioOutput(((videoRef: any): MediaRef))
	const videoVolume = useVolumeOf(VOLUMES.VIDEO)
	const updateVolumes = useCallback(() => {
		if (videoRef.current) {
			// $FlowFixMe[incompatible-use] we know that volume is on the HTMLVideoElement
			videoRef.current.volume = videoVolume
		}
	}, [videoRef, videoVolume])
	useEffect(() => {
		updateVolumes()
	}, [updateVolumes])
	return (
		<StyledVideo
			autoPlay
			{...extraProps}
			ref={videoRef}
			muted={isSilent || muted}
			onPlay={() => {
				onPlay?.()
				if (!isSilent) {
					updateVolumes()
				}
			}}>
			<source src={url} />
		</StyledVideo>
	)
}): AbstractComponent<VideoProps, HTMLVideoElement>)
